import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { ChatChannelFilter } from './useGetChats';
import { GetNotificationsResponse } from './useGetNotifications';

interface MarkAllChatsReadInput {
    /** Channel scope to clear. 'all' clears both internal + vendor. */
    channel: ChatChannelFilter;
    /**
     * Number of unread message notifications being cleared for a single-channel clear,
     * used to optimistically decrement the TopBar New Chat badge (`unreadMentionCount`).
     * Ignored when channel is 'all' (the badge is zeroed instead).
     */
    clearedCount?: number;
}

const markAllChatsRead = async (sessionId: string, input: MarkAllChatsReadInput): Promise<void> => {
    await axios.post(
        `${API_URL}/chats/read-all`,
        { channel: input.channel === 'all' ? 'all' : input.channel },
        { headers: { Authorization: sessionId } },
    );
};

export const useMarkAllChatsRead = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: MarkAllChatsReadInput) => {
            if (!sessionContext?.sessionId) throw new Error('Not authenticated');
            return markAllChatsRead(sessionContext.sessionId, input);
        },
        // Optimistically drop the TopBar New Chat badge so the count updates instantly,
        // before the /notifications poll reconciles.
        onMutate: async (input) => {
            await queryClient.cancelQueries({ queryKey: ['/notifications'] });
            const previous = queryClient.getQueriesData<GetNotificationsResponse>({ queryKey: ['/notifications'] });
            queryClient.setQueriesData<GetNotificationsResponse>({ queryKey: ['/notifications'] }, (old) => {
                if (!old) return old;
                const current = old.unreadMentionCount ?? 0;
                const next = input.channel === 'all'
                    ? 0
                    : Math.max(0, current - (input.clearedCount ?? 0));
                return { ...old, unreadMentionCount: next };
            });
            return { previous };
        },
        onError: (_err, _input, context) => {
            for (const [key, data] of context?.previous ?? []) {
                queryClient.setQueryData(key, data);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['/notifications'] });
        },
    });
};
